import { useEffect, useRef, useState } from 'react';

/* ==================================================================
   useInView
   ------------------------------------------------------------------
   Reveal trigger. Fires once by default and then disconnects, so a
   revealed card costs nothing for the rest of the session. Falls back
   to "visible" when IntersectionObserver is missing, which means no
   content is ever trapped behind an absent API.
   ================================================================== */

export default function useInView(options) {
  /* threshold 0 with a small negative bottom margin, deliberately: a ratio
     threshold can never be met by an element taller than the viewport, which
     would leave a long results grid permanently at opacity 0. */
  const {
    threshold = 0,
    rootMargin = '0px 0px -40px 0px',
    once = true,
    skip = false,
  } = options || {};

  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (skip) {
      setInView(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once, skip]);

  return [ref, inView];
}
